/**
 * Хедер главной страницы
 */

import { useRef, useEffect, useCallback } from 'react';
import { NotificationOutlineIcon } from '@/assets/icons';
import { useCurrentUser } from '@/features/user';
import { useNotificationsQuery } from '@/features/notifications';
import { useRouterNavigation } from '@/lib';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components';
import { Button } from '@/components/ui/button';
import type { HomeHeaderProps } from '../model';

/**
 * Header главной страницы с вкладками, иконкой уведомлений и сортировкой
 */
export function HomeHeader({
  currentTab,
  tabs,
  onTabChange,
}: HomeHeaderProps): JSX.Element {
  const { t } = useTranslation();
  const { navigate } = useRouterNavigation();

  // Рефы для автоскролла табов
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const setTabRef = useCallback((id: string, el: HTMLButtonElement | null) => {
    if (el) {
      tabRefs.current.set(id, el);
    } else {
      tabRefs.current.delete(id);
    }
  }, []);

  // Автоскролл к выбранному табу с отступом
  useEffect(() => {
    const container = scrollContainerRef.current;
    const tabEl = tabRefs.current.get(currentTab);
    if (!container || !tabEl) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = tabEl.getBoundingClientRect();

    // Отступ от краёв контейнера (px)
    const SCROLL_PADDING = 16;

    // Если таб выходит за правый край — скроллим вправо
    if (tabRect.right > containerRect.right - SCROLL_PADDING) {
      container.scrollTo({
        left: container.scrollLeft + (tabRect.right - containerRect.right) + SCROLL_PADDING,
        behavior: 'smooth',
      });
    }
    // Если таб выходит за левый край — скроллим влево
    else if (tabRect.left < containerRect.left + SCROLL_PADDING) {
      container.scrollTo({
        left: container.scrollLeft - (containerRect.left - tabRect.left) - SCROLL_PADDING,
        behavior: 'smooth',
      });
    }
  }, [currentTab]);

  // Получаем текущего пользователя и его уведомления
  const { data: currentUser } = useCurrentUser();
  const { data: notifications } = useNotificationsQuery(currentUser?.id);

  // Проверяем есть ли непрочитанные уведомления
  const hasUnreadNotifications = notifications?.some((n) => !n.isRead) ?? false;

  return (
    <>
      {/* Header с кнопками */}
      <Header
        title={t('home:home.title')}
        rightAction={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon-header"
              onClick={() => navigate('/notifications')}
              className="active:scale-90 transition-transform relative"
              aria-label={t('common:notifications')}
            >
              <NotificationOutlineIcon />
              {hasUnreadNotifications && (
                <div className="absolute top-1 right-1 w-2.5 h-2.5 bg-[var(--color-accent)] rounded-full" />
              )}
            </Button>
          </div>
        }
      />

      {/* Вкладки */}
      <div className="bg-background overflow-x-hidden">
        <div className="pb-0">
          <div
            ref={scrollContainerRef}
            className="flex gap-2 items-center overflow-x-auto no-scrollbar"
          >
            {/* Левый отступ (скроллится вместе с контентом) */}
            <div className="w-2 flex-shrink-0" />
            {/* Табы */}
            {tabs.map((tab) => (
              <Button
                key={tab.id}
                ref={(el) => setTabRef(tab.id, el)}
                onClick={() => onTabChange(tab.id)}
                variant="ghost"
                className={`px-5 h-9 rounded-full text-base transition-all flex-shrink-0 ${
                  currentTab === tab.id
                    ? 'bg-[var(--color-accent)] text-white font-semibold'
                    : 'bg-card text-[var(--primary-purple)] font-semibold'
                }`}
              >
                {t(tab.labelKey)}
              </Button>
            ))}
            {/* Правый отступ (скроллится вместе с контентом) */}
            <div className="w-2 flex-shrink-0" />
          </div>
        </div>
      </div>
    </>
  );
}